// React Imports
import { useState, useEffect, useRef } from "react";

// Toolkit Imports
import { initPathCompletedChannel } from "sj-ui-toolkit/src/client/actionCable";
import { fetchUserPathData, PassedBelt } from "sj-ui-toolkit/src/api";

type PathCompletedChannel = Awaited<ReturnType<typeof initPathCompletedChannel>>;

/**
 * Hook that subscribes to the PathCompletedChannel via Action Cable.
 *
 * When the server broadcasts a path-completion event (during a lesson),
 * it silently stores the data. The consuming component can check
 * `isPathCompleted` after a lesson finishes to decide whether to show
 * the path completion screen.
 */
export function usePathCompletion() {
  const [isPathCompleted, setIsPathCompleted] = useState(false);
  const [completedPath, setCompletedPath] = useState<PassedBelt | null>(null);

  const cableRef = useRef<PathCompletedChannel | null>(null);

  useEffect(() => {
    let isDisposed = false;

    void initPathCompletedChannel(async data => {
      if (data?.status !== "success") return;

      try {
        const { passedBelts } = await fetchUserPathData();
        const roleEnrollmentId = data.roleEnrollmentId;
        const matchingPath = passedBelts?.find(path => path.id === roleEnrollmentId);

        setCompletedPath(matchingPath ?? null);
        setIsPathCompleted(true);
      } catch (error) {
        console.error("Error fetching path data after completion:", error);
      }
    })
      .then(subscription => {
        if (isDisposed) {
          subscription.unsubscribe();
          return;
        }
        cableRef.current = subscription;
      })
      .catch(error => {
        console.error("Unable to subscribe to the path completion channel", error);
      });

    return () => {
      isDisposed = true;
      if (cableRef.current) {
        cableRef.current.unsubscribe();
        cableRef.current = null;
      }
    };
  }, []);

  return {
    isPathCompleted,
    completedPath,
  };
}
